/**
 * GuestPartyPanel — las personas de una reserva, para el equipo.
 *
 * Lo usa la ficha de reserva y la pantalla de Huéspedes, así que las dos
 * enseñan exactamente lo mismo: una sola fuente, un solo render.
 *
 * Resumen primero y detalle a petición. Recepción no tiene que leerse una
 * lista de 14 casillas para saber qué le falta a una llegada: arriba va la
 * frase, y lo que falta se despliega.
 *
 * Del documento solo sale el ESTADO. Ni número ni imagen, ni aquí ni en
 * ninguna pantalla operativa (§7.3.5).
 */
import React, { useState } from "react";
import { Check, CaretDown, ShieldCheck, Warning } from "@phosphor-icons/react";
import { GUEST_FIELD_LABELS } from "@/data/settingsV2Demo";
import {
  PRE_CHECKIN_LABEL, PRE_CHECKIN_TONE, IMMIGRATION_LABEL,
  DOCUMENT_STATUS_LABEL, ARRIVAL_MODE_LABEL,
} from "@/data/guestsV2Demo";
import { transferSummaryOf } from "@/lib/guestsV2Selectors";
import { StatusPill, Meta, QuickAction } from "@/pages/admin/pms/v2/V2Primitives";

/**
 * Estados desde los que recepción puede comprobar el documento en persona.
 * `declared` es lo máximo que da el pre-check-in de esta vista previa: el
 * huésped confirma que lo trae, y recepción lo comprueba al llegar.
 */
const VERIFIABLE = ["declared", "provided"];

export default function GuestPartyPanel({
  view, onVerifyDocument, onFileImmigration, compact = false,
}) {
  const [openFields, setOpenFields] = useState(false);
  const [openPeople, setOpenPeople] = useState(!compact);
  if (!view) return null;

  const { party, missing, immigration, arrival, preferences } = view;

  return (
    <div data-testid="guest-party-panel">
      {/* Una línea: cuántos son, cómo va el pre-check-in y cómo va el reporte. */}
      <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="guest-party-status">
        <StatusPill tone="stone" dot={false}>
          {party.declared === 1 ? "1 huésped" : `${party.declared} huéspedes`}
          {party.unnamed > 0 ? ` · ${party.unnamed} sin registrar` : ""}
        </StatusPill>
        <StatusPill tone={PRE_CHECKIN_TONE[view.pre_checkin] || "stone"} dot={false} testId="guest-precheckin-pill">
          {PRE_CHECKIN_LABEL[view.pre_checkin]}
          {missing.length > 0 ? ` · faltan ${missing.length}` : ""}
        </StatusPill>
        {immigration.tracked && (
          <StatusPill
            tone={immigration.state === "filed" ? "emerald" : "amber"}
            dot={false}
            testId="guest-immigration-pill"
          >
            APOA: {IMMIGRATION_LABEL[immigration.state].toLowerCase()}
          </StatusPill>
        )}
      </div>

      {/* Lo que falta, nombrado. Solo si falta algo. */}
      {missing.length > 0 && (
        <div className="mb-3">
          <button
            type="button"
            onClick={() => setOpenFields((v) => !v)}
            aria-expanded={openFields}
            data-testid="guest-missing-toggle"
            className="min-h-[44px] inline-flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300"
          >
            <Warning size={13} />
            {missing.length === 1 ? "Falta 1 dato" : `Faltan ${missing.length} datos`}
            <CaretDown size={12} className={openFields ? "rotate-180" : ""} />
          </button>
          {openFields && (
            <ul className="mt-1 space-y-0.5" data-testid="guest-missing-list">
              {missing.map((m) => (
                <li key={`${m.scope}-${m.field}-${m.guest_id || "party"}`} className="text-xs text-stone-600 dark:text-stone-300">
                  {GUEST_FIELD_LABELS[m.field] || m.field}
                  {m.guest_name ? <span className="text-stone-400"> · {m.guest_name}</span> : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Llegada y traslado: lo que decide cómo se prepara el día. */}
      {arrival && (
        <div className="mb-3 text-xs text-stone-600 dark:text-stone-300" data-testid="guest-arrival-summary">
          <Meta items={[
            ARRIVAL_MODE_LABEL[arrival.mode],
            arrival.reference || null,
            arrival.landing_time ? `aterriza ${arrival.landing_time}` : null,
            arrival.estimated_arrival ? `llega ${arrival.estimated_arrival}` : null,
            // A quien llega por su cuenta no se le pregunta por el traslado, así
            // que aquí no aparece ni como pendiente.
            transferSummaryOf(arrival),
          ]} />
        </div>
      )}

      {/* Preferencias que cambian la operativa. Nunca el motivo de nadie. */}
      {view.operational_notes.length > 0 && (
        <ul className="mb-3 space-y-1" data-testid="guest-operational-notes">
          {view.operational_notes.map((n) => (
            <li key={n.key} className="text-xs" data-testid={`guest-note-${n.key}`}>
              <span className="text-stone-400">{n.label}: </span>
              <span className={n.tone === "red"
                ? "text-red-700 dark:text-red-300"
                : "text-stone-700 dark:text-stone-300"}>{n.value}</span>
            </li>
          ))}
        </ul>
      )}
      {preferences?.notes ? (
        <p className="mb-3 text-xs text-stone-500 dark:text-stone-400" data-testid="guest-free-notes">
          {preferences.notes}
        </p>
      ) : null}

      {/* Las personas, detrás de un toque. */}
      <button
        type="button"
        onClick={() => setOpenPeople((v) => !v)}
        aria-expanded={openPeople}
        data-testid="guest-people-toggle"
        className="min-h-[44px] inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-900 dark:hover:text-stone-100"
      >
        Ver las personas <CaretDown size={12} className={openPeople ? "rotate-180" : ""} />
      </button>

      {openPeople && (
        <ul className="mt-1 divide-y divide-stone-100 dark:divide-stone-800" data-testid="guest-people-list">
          {view.guests.map((g) => (
            <li key={g.guest_id} className="py-2 flex items-start justify-between gap-3" data-testid={`guest-row-${g.guest_id}`}>
              <div className="min-w-0">
                <div className="text-sm text-stone-900 dark:text-stone-100">
                  {g.full_name}
                  {g.primary_guest && <span className="text-stone-400 text-xs"> · principal</span>}
                </div>
                <Meta items={[
                  g.nationality || "Nacionalidad no disponible",
                  `Documento: ${DOCUMENT_STATUS_LABEL[g.document?.status || "not_provided"].toLowerCase()}`,
                  g.primary_guest ? (g.email || "Sin email") : null,
                  g.primary_guest ? (g.phone || "Sin teléfono") : null,
                ]} />
              </div>
              {onVerifyDocument && VERIFIABLE.includes(g.document?.status) && (
                <QuickAction
                  label="Comprobar"
                  icon={ShieldCheck}
                  size="sm"
                  onClick={() => onVerifyDocument(g.guest_id)}
                  testId={`guest-verify-${g.guest_id}`}
                />
              )}
              {g.document?.status === "verified" && (
                <Check size={14} className="text-emerald-600 shrink-0 mt-1" aria-label="Documento comprobado" />
              )}
            </li>
          ))}
        </ul>
      )}

      <p className="text-[11px] text-stone-400 mt-2" data-testid="guest-privacy-note">
        Aquí solo se ve el estado del documento. Ni el número ni la imagen se
        muestran ni se descargan desde una pantalla operativa. En esta vista
        previa el huésped no sube ningún archivo: «confirmado por el huésped»
        quiere decir que dice traerlo, y se comprueba en recepción.
      </p>

      {/* El reporte lo presenta la propiedad, y solo desde aquí pasa a presentado. */}
      {onFileImmigration && immigration.tracked && immigration.state === "due" && (
        <div className="mt-3 pt-3 border-t border-stone-100 dark:border-stone-800">
          <QuickAction
            label="Marcar reporte presentado"
            onClick={onFileImmigration}
            testId="guest-file-immigration"
          />
          <p className="text-[11px] text-stone-400 mt-1">
            El reporte de extranjeros lo presenta la propiedad. No lo mueve ni
            el check-in del huésped ni su entrada.
          </p>
        </div>
      )}
      {immigration.state === "filed" && immigration.filed_at && (
        <p className="text-[11px] text-stone-400 mt-2" data-testid="guest-immigration-filed">
          Presentado el {String(immigration.filed_at).slice(0, 10)}
          {immigration.actor ? ` · ${immigration.actor}` : ""}
        </p>
      )}
    </div>
  );
}
